import { useModalQueue } from '@carvana/merch-react-hooks';
import { MoreDetailsModalSections } from '@carvana/merch-tile';
import deepmerge from 'deepmerge';
import React, { createContext, useCallback, useMemo, useState } from 'react';
import type { DeepPartial } from '~/types';
import { ModalId, ModalPriorities } from './constants';

const dummyFn = () => {};

type Toggler = (isOpen: boolean) => void;
type WithContinue = { onContinue?: () => void };
type WithConfirmationContent = { onlyConfirmCancellation?: boolean };
type ReservableNotificationModalOptions = { isSubscribed?: boolean };

export type MoreDetailsOptions = {
  openTo: MoreDetailsModalSections;
  vehicleId?: number;
};

type ContextType = {
  confirmModalContinue: () => void;
  isAccountModalOpen: boolean;
  isAnyModalOpen: boolean;
  isAvailabilityModalOpen: boolean;
  isConfirmModalOpen: boolean;
  isEvTaxCreditModalOpen: boolean;
  isFeaturesAndSpecsModalOpen: boolean;
  isFinanceActivationModalOpen: boolean;
  isGeolocationModalOpen: boolean;
  isKeyFeaturesModalOpen: boolean;
  isMoreDetailsModalOpen: boolean;
  isLockTimerModalOpen: boolean;
  isPartnerModalOpen: boolean;
  isPetitionModalOpen: boolean;
  isPreOrderModalOpen: boolean;
  isReservableCancelModalOpen: boolean;
  isReservableModalOpen: boolean;
  isReservableNotificationModalOpen: boolean;
  isReservableNotificationSubscribed: boolean;
  isReturnPolicyModalOpen: boolean;
  isSimilarVehiclesModalOpen: boolean;
  isVehicleInquiryModalOpen: boolean;
  keyFeaturesOpenTo: number;
  moreDetailsOptions: MoreDetailsOptions;
  onlyConfirmCancellation: boolean;
  openToOEM: boolean;
  partnerModalContinue: () => void;
  setAccountModalOpen: Toggler;
  setAvailabilityModalOpen: Toggler;
  setConfirmModalOpen: (
    toggle: boolean,
    options?: WithContinue & WithConfirmationContent
  ) => void;
  setEvTaxCreditModalOpen: Toggler;
  setFeaturesAndSpecsModalOpen: (toggle: boolean, openToOEM?: boolean) => void;
  setFinanceActivationModalOpen: Toggler;
  setGeolocationModalOpen: Toggler;
  setLockTimerModalOpen: Toggler;
  setMoreDetailsModalOpen: (
    isOpen: boolean,
    options?: MoreDetailsOptions
  ) => void;
  setKeyFeaturesModalOpen: (toggle: boolean, openTo?: number) => void;
  setPartnerModalOpen: (toggle: boolean, options?: WithContinue) => void;
  setPetitionModalOpen: Toggler;
  setPreOrderModalOpen: Toggler;
  setReservableCancelModalOpen: Toggler;
  setReservableModalOpen: Toggler;
  setReservableNotificationModalOpen: (
    toggle: boolean,
    options?: ReservableNotificationModalOptions
  ) => void;
  setReturnPolicyModalOpen: Toggler;
  setSimilarVehiclesModal: Toggler;
  setVehicleInquiryModalOpen: Toggler;
};

const defaultContext: ContextType = {
  confirmModalContinue: dummyFn,
  isAccountModalOpen: false,
  isAnyModalOpen: false,
  isAvailabilityModalOpen: false,
  isConfirmModalOpen: false,
  isEvTaxCreditModalOpen: false,
  isFeaturesAndSpecsModalOpen: false,
  isFinanceActivationModalOpen: false,
  isGeolocationModalOpen: false,
  isKeyFeaturesModalOpen: false,
  isLockTimerModalOpen: false,
  isMoreDetailsModalOpen: false,
  isPartnerModalOpen: false,
  isPetitionModalOpen: false,
  isPreOrderModalOpen: false,
  isReservableCancelModalOpen: false,
  isReservableModalOpen: false,
  isReservableNotificationModalOpen: false,
  isReservableNotificationSubscribed: false,
  isReturnPolicyModalOpen: false,
  isSimilarVehiclesModalOpen: false,
  isVehicleInquiryModalOpen: false,
  keyFeaturesOpenTo: 0,
  moreDetailsOptions: {
    openTo: MoreDetailsModalSections.PriceDetails,
    vehicleId: undefined
  },
  onlyConfirmCancellation: false,
  openToOEM: false,
  partnerModalContinue: dummyFn,
  setAccountModalOpen() {},
  setAvailabilityModalOpen() {},
  setConfirmModalOpen() {},
  setEvTaxCreditModalOpen() {},
  setFeaturesAndSpecsModalOpen() {},
  setFinanceActivationModalOpen() {},
  setGeolocationModalOpen() {},
  setKeyFeaturesModalOpen() {},
  setLockTimerModalOpen() {},
  setMoreDetailsModalOpen() {},
  setPartnerModalOpen() {},
  setPetitionModalOpen() {},
  setPreOrderModalOpen() {},
  setReservableCancelModalOpen() {},
  setReservableModalOpen() {},
  setReservableNotificationModalOpen() {},
  setReturnPolicyModalOpen() {},
  setSimilarVehiclesModal() {},
  setVehicleInquiryModalOpen() {}
};

type DummyFnType = typeof dummyFn;

const ModalContext = createContext<ContextType>(defaultContext);

export function ModalContextProvider({ children }) {
  const [currentModalId, generateOpener] = useModalQueue(ModalPriorities);
  const [keyFeaturesOpenTo, setKeyFeaturesOpenTo] = useState<number>(
    defaultContext.keyFeaturesOpenTo
  );
  const [openToOEM, setOpenToOEM] = useState<boolean>(defaultContext.openToOEM);
  const [moreDetailsOptions, setMoreDetailsOptions] =
    useState<MoreDetailsOptions>(defaultContext.moreDetailsOptions);

  const {
    setAccountModalOpen,
    setAvailabilityModalOpen,
    setConfirmModalOpen,
    setEvTaxCreditModalOpen,
    setFeaturesAndSpecsModalOpen,
    setFinanceActivationModalOpen,
    setGeolocationModalOpen,
    setKeyFeaturesModalOpen,
    setMoreDetailsModalOpen,
    setLockTimerModalOpen,
    setPartnerModalOpen,
    setPetitionModalOpen,
    setPreOrderModalOpen,
    setReservableCancelModalOpen,
    setReservableModalOpen,
    setReservableNotificationModalOpen,
    setReturnPolicyModalOpen,
    setSimilarVehiclesModal,
    setVehicleInquiryModalOpen
  } = useMemo(
    () => ({
      setAccountModalOpen: generateOpener(ModalId.Account),
      setAvailabilityModalOpen: generateOpener(ModalId.Availability),
      setConfirmModalOpen: generateOpener(ModalId.Confirm),
      setEvTaxCreditModalOpen: generateOpener(ModalId.EvTaxCredit),
      setFeaturesAndSpecsModalOpen: generateOpener(ModalId.FeaturesAndSpecs),
      setFinanceActivationModalOpen: generateOpener(ModalId.FinanceActivation),
      setGeolocationModalOpen: generateOpener(ModalId.GeoLocation),
      setKeyFeaturesModalOpen: generateOpener(ModalId.KeyFeatures),
      setLockTimerModalOpen: generateOpener(ModalId.LockTimer),
      setMoreDetailsModalOpen: generateOpener(ModalId.MoreDetails),
      setPartnerModalOpen: generateOpener(ModalId.Partner),
      setPetitionModalOpen: generateOpener(ModalId.Petition),
      setPreOrderModalOpen: generateOpener(ModalId.PreOrder),
      setReservableCancelModalOpen: generateOpener(ModalId.ReservableCancel),
      setReservableModalOpen: generateOpener(ModalId.Reservable),
      setReservableNotificationModalOpen: generateOpener(
        ModalId.ReservableNotification
      ),
      setReturnPolicyModalOpen: generateOpener(ModalId.ReturnPolicy),
      setSimilarVehiclesModal: generateOpener(ModalId.SimilarVehicles),
      setVehicleInquiryModalOpen: generateOpener(ModalId.Activation)
    }),
    [generateOpener]
  );

  //Key Features
  const setKeyFeaturesOpenWithOptions = useCallback<
    ContextType['setKeyFeaturesModalOpen']
  >(
    (isOpen: boolean, openTo: number = defaultContext.keyFeaturesOpenTo) => {
      setKeyFeaturesOpenTo(openTo);
      setKeyFeaturesModalOpen(isOpen);
    },
    [setKeyFeaturesModalOpen]
  );

  // Features and Specs
  const setFeaturesAndSpecsOpenWithOptions = useCallback<
    ContextType['setFeaturesAndSpecsModalOpen']
  >(
    (isOpen: boolean, openToOEMPanel: boolean = defaultContext.openToOEM) => {
      setOpenToOEM(openToOEMPanel);
      setFeaturesAndSpecsModalOpen(isOpen);
    },
    [setFeaturesAndSpecsModalOpen, setOpenToOEM]
  );

  // Reservable
  const [
    isReservableNotificationSubscribed,
    setReservableNotificationSubscribed
  ] = useState(false);

  const setReservableNotificationModalOpenWithOptions = useCallback(
    (
      isOpen: boolean,
      { isSubscribed = false }: ReservableNotificationModalOptions = {}
    ) => {
      setReservableNotificationModalOpen(isOpen);
      setReservableNotificationSubscribed(isSubscribed);
    },
    [setReservableNotificationModalOpen]
  );

  // Cancel purchase, diesel, manual transmission confirmation
  const [confirmModalContinue, setConfirmModalContinue] = useState<DummyFnType>(
    defaultContext.confirmModalContinue
  );
  const [onlyConfirmCancellation, setOnlyConfirmCancellation] = useState(
    defaultContext.onlyConfirmCancellation
  );

  const setConfirmModalOpenWithOptions = useCallback(
    (open, options: WithContinue & WithConfirmationContent = {}) => {
      setConfirmModalOpen(open);
      setConfirmModalContinue(() => options.onContinue ?? dummyFn);
      setOnlyConfirmCancellation(options.onlyConfirmCancellation ?? false);
    },
    [setConfirmModalOpen]
  );

  // Partner confirmation
  const [partnerModalContinue, setPartnerModalContinue] =
    useState<DummyFnType>(dummyFn);

  const setPartnerOpenWithOptions = useCallback(
    (open, { onContinue = dummyFn }: WithContinue = {}) => {
      setPartnerModalOpen(open);
      setPartnerModalContinue(() => onContinue);
    },
    [setPartnerModalOpen]
  );

  //More Details Modal
  const setMoreDetailsModalOpenWithVehicle = useCallback<
    ContextType['setMoreDetailsModalOpen']
  >(
    (
      isOpen: boolean,
      options: MoreDetailsOptions = defaultContext.moreDetailsOptions
    ) => {
      setMoreDetailsOptions(options);
      setMoreDetailsModalOpen(isOpen);
    },
    [setMoreDetailsModalOpen]
  );

  const value = useMemo<ContextType>(
    (): ContextType => ({
      confirmModalContinue,
      isAccountModalOpen: currentModalId === ModalId.Account,
      isAnyModalOpen: currentModalId > -1,
      isAvailabilityModalOpen: currentModalId === ModalId.Availability,
      isConfirmModalOpen: currentModalId === ModalId.Confirm,
      isEvTaxCreditModalOpen: currentModalId === ModalId.EvTaxCredit,
      isFeaturesAndSpecsModalOpen: currentModalId === ModalId.FeaturesAndSpecs,
      isFinanceActivationModalOpen:
        currentModalId === ModalId.FinanceActivation,
      isGeolocationModalOpen: currentModalId === ModalId.GeoLocation,
      isKeyFeaturesModalOpen: currentModalId === ModalId.KeyFeatures,
      isLockTimerModalOpen: currentModalId === ModalId.LockTimer,
      isMoreDetailsModalOpen: currentModalId === ModalId.MoreDetails,
      isPartnerModalOpen: currentModalId === ModalId.Partner,
      isPetitionModalOpen: currentModalId === ModalId.Petition,
      isPreOrderModalOpen: currentModalId === ModalId.PreOrder,
      isReservableCancelModalOpen: currentModalId === ModalId.ReservableCancel,
      isReservableModalOpen: currentModalId === ModalId.Reservable,
      isReservableNotificationModalOpen:
        currentModalId === ModalId.ReservableNotification,
      isReservableNotificationSubscribed,
      isReturnPolicyModalOpen: currentModalId === ModalId.ReturnPolicy,
      isSimilarVehiclesModalOpen: currentModalId === ModalId.SimilarVehicles,
      isVehicleInquiryModalOpen: currentModalId === ModalId.Activation,
      keyFeaturesOpenTo,
      moreDetailsOptions,
      onlyConfirmCancellation,
      openToOEM,
      partnerModalContinue,
      setAccountModalOpen,
      setAvailabilityModalOpen,
      setConfirmModalOpen: setConfirmModalOpenWithOptions,
      setEvTaxCreditModalOpen,
      setFeaturesAndSpecsModalOpen: setFeaturesAndSpecsOpenWithOptions,
      setFinanceActivationModalOpen,
      setGeolocationModalOpen,
      setKeyFeaturesModalOpen: setKeyFeaturesOpenWithOptions,
      setLockTimerModalOpen,
      setMoreDetailsModalOpen: setMoreDetailsModalOpenWithVehicle,
      setPartnerModalOpen: setPartnerOpenWithOptions,
      setPetitionModalOpen,
      setPreOrderModalOpen,
      setReservableCancelModalOpen,
      setReservableModalOpen,
      setReservableNotificationModalOpen:
        setReservableNotificationModalOpenWithOptions,
      setReturnPolicyModalOpen,
      setSimilarVehiclesModal,
      setVehicleInquiryModalOpen
    }),
    [
      confirmModalContinue,
      currentModalId,
      isReservableNotificationSubscribed,
      keyFeaturesOpenTo,
      moreDetailsOptions,
      onlyConfirmCancellation,
      openToOEM,
      partnerModalContinue,
      setAccountModalOpen,
      setAvailabilityModalOpen,
      setConfirmModalOpenWithOptions,
      setEvTaxCreditModalOpen,
      setFeaturesAndSpecsOpenWithOptions,
      setFinanceActivationModalOpen,
      setGeolocationModalOpen,
      setKeyFeaturesOpenWithOptions,
      setLockTimerModalOpen,
      setMoreDetailsModalOpenWithVehicle,
      setPartnerOpenWithOptions,
      setPetitionModalOpen,
      setPreOrderModalOpen,
      setReservableCancelModalOpen,
      setReservableModalOpen,
      setReservableNotificationModalOpenWithOptions,
      setReturnPolicyModalOpen,
      setSimilarVehiclesModal,
      setVehicleInquiryModalOpen
    ]
  );

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
}

ModalContextProvider.displayName = 'ModalContextProvider';

export default ModalContext;

export const generateModalContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function useModalContext() {
  return React.useContext(ModalContext);
}
